// Gamification service - handles badges, achievements, streaks, and leaderboards
import { db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, collection, query, getDocs, orderBy, limit, serverTimestamp, increment, Timestamp } from 'firebase/firestore';

/**
 * Beta configuration
 * Set to false when beta phase is over to stop automatically awarding early adopter badge
 */
const BETA_IS_ACTIVE = true;

/**
 * Badge definitions
 */
export const BADGES = {
  // Profile completeness badges
  PROFILE_STARTER: { id: 'profile_starter', name: 'Profile Starter', description: 'Complete 25% of your profile', icon: '⭐', color: '#ffc107', points: 15 },
  PROFILE_BUILDER: { id: 'profile_builder', name: 'Profile Builder', description: 'Complete 50% of your profile', icon: '🏗️', color: '#4a90e2', points: 20 },
  PROFILE_MASTER: { id: 'profile_master', name: 'Profile Master', description: 'Complete 75% of your profile', icon: '👑', color: '#9c27b0', points: 35 },
  PROFILE_LEGEND: { id: 'profile_legend', name: 'Profile Legend', description: 'Complete 100% of your profile', icon: '🌟', color: '#ff9800', points: 75 },
  
  // Activity badges
  FIRST_STEPS: { id: 'first_steps', name: 'First Steps', description: 'Visit the platform for the first time', icon: '👣', color: '#4caf50', points: 5 },
  WEEK_WARRIOR: { id: 'week_warrior', name: 'Week Warrior', description: 'Maintain a 7-day activity streak', icon: '🔥', color: '#f44336', points: 25 },
  MONTH_MASTER: { id: 'month_master', name: 'Month Master', description: 'Maintain a 30-day activity streak', icon: '💪', color: '#e91e63', points: 40 },
  STREAK_CHAMPION: { id: 'streak_champion', name: 'Streak Champion', description: 'Maintain a 100-day activity streak', icon: '🏆', color: '#ff5722', points: 100 },
  
  // Social badges
  SOCIAL_BUTTERFLY: { id: 'social_butterfly', name: 'Social Butterfly', description: 'Add 5 social links', icon: '🦋', color: '#00bcd4', points: 20 },
  TAG_COLLECTOR: { id: 'tag_collector', name: 'Tag Collector', description: 'Add 10 tags to your profile', icon: '🏷️', color: '#3f51b5', points: 20 },
  CONNECTOR: { id: 'connector', name: 'Connector', description: 'Send your first message', icon: '💬', color: '#009688', points: 10 },
  X_CONNECTOR: { id: 'x_connector', name: 'X Connector', description: 'Connect your X.com account', icon: '🐦', color: '#000000', points: 10 },
  DISCORD_CONNECTOR: { id: 'discord_connector', name: 'Discord Connector', description: 'Connect your Discord account', icon: '💬', color: '#5865F2', points: 10 },
  BULLISH_POSTER: { id: 'bullish_poster', name: 'Bullish Poster', description: 'Post about PeopleTag on X.com', icon: '📈', color: '#ff9800', points: 300 },
  
  // Achievement badges
  EARLY_ADOPTER: { id: 'early_adopter', name: 'Early Adopter', description: 'Join during beta phase', icon: '🚀', color: '#673ab7', points: 500 },
  EXPLORER: { id: 'explorer', name: 'Explorer', description: 'View 50 profiles', icon: '🗺️', color: '#795548', points: 45 },
  
  // Referral badges
  REFERRAL_STARTER: { id: 'referral_starter', name: 'Referral Starter', description: 'Invite 1 friend', icon: '👥', color: '#4caf50', points: 10 },
  REFERRAL_CHAMPION: { id: 'referral_champion', name: 'Referral Champion', description: 'Invite 10 friends', icon: '🌟', color: '#4a90e2', points: 50 },
  REFERRAL_LEGEND: { id: 'referral_legend', name: 'Referral Legend', description: 'Invite 100 friends', icon: '👑', color: '#9c27b0', points: 200 },
  REFERRAL_MASTER: { id: 'referral_master', name: 'Referral Master', description: 'Invite 1000 friends', icon: '🏆', color: '#ff5722', points: 1000 },
};

/**
 * Calculate profile completeness percentage
 */
export const calculateProfileCompleteness = (profile) => {
  if (!profile) return 0;
  
  // Define field names for logging
  const fieldNames = [
    'username',
    'name',
    'bio',
    'city',
    'birthDate',
    'tagsList',
    'socialLinks',
    'relationshipStatus',
    'lookingFor',
    'languages',
    'education',
    'occupation',
    'email',
    'preferredContactMethod'
  ];
  
  // Required fields (must be filled for 100%)
  const requiredFields = [
    profile.username,
    profile.name,
    profile.bio,
    profile.city,
    profile.birthDate,
    profile.tagsList && profile.tagsList.length > 0,
    profile.socialLinks && profile.socialLinks.length > 0,
    profile.relationshipStatus,
    profile.lookingFor,
    profile.languages,
    profile.education,
    profile.occupation,
    profile.email,
    profile.preferredContactMethod
  ];
  
  // Optional fields (bonus points, but not required for 100%)
  const optionalFields = [
    profile.role // Job Title is optional
  ];
  
  // Check each field individually and track missing ones
  const missingFields = [];
  const fieldChecks = requiredFields.map((field, index) => {
    let isComplete = false;
    if (typeof field === 'boolean') {
      isComplete = field;
    } else if (Array.isArray(field)) {
      isComplete = field.length > 0;
    } else {
      isComplete = field && field.toString().trim().length > 0;
    }
    
    if (!isComplete) {
      missingFields.push(fieldNames[index]);
    }
    
    return isComplete;
  });
  
  const completedRequired = fieldChecks.filter(Boolean).length;
  
  const completedOptional = optionalFields.filter(field => {
    if (typeof field === 'boolean') return field;
    if (Array.isArray(field)) return field.length > 0;
    return field && field.toString().trim().length > 0;
  }).length;
  
  // Calculate percentage: required fields count for 100%, optional fields are bonus
  // If all required fields are filled, you get 100% (even if optional fields are missing)
  const requiredPercentage = (completedRequired / requiredFields.length) * 100;
  const finalPercentage = Math.min(100, Math.round(requiredPercentage));
  
  // Log missing fields for debugging
  if (missingFields.length > 0) {
    console.log('📊 Profile Completeness Debug:', {
      percentage: finalPercentage + '%',
      completed: `${completedRequired}/${requiredFields.length}`,
      missingFields: missingFields,
      fieldDetails: missingFields.map(fieldName => {
        const value = profile[fieldName];
        return {
          field: fieldName,
          value: value,
          type: typeof value,
          isEmpty: !value || (typeof value === 'string' && value.trim().length === 0) || (Array.isArray(value) && value.length === 0)
        };
      })
    });
  } else {
    console.log('✅ Profile Completeness: 100% - All required fields are filled!');
  }
  
  // Cap at 100% (optional fields don't go above 100%)
  return finalPercentage;
};

/**
 * Calculate points from badges based on current badge definitions
 */
const calculatePointsFromBadges = (badgeIds) => {
  if (!Array.isArray(badgeIds)) return 0;
  
  return badgeIds.reduce((sum, badgeId) => {
    const badgeKey = Object.keys(BADGES).find(key => BADGES[key].id === badgeId);
    const badge = badgeKey ? BADGES[badgeKey] : null;
    return sum + (badge?.points || 0);
  }, 0);
};

/**
 * Recalculate and update user's points based on current badge definitions
 */
export const recalculatePointsFromBadges = async (walletAddress) => {
  if (!walletAddress) return null;
  
  try {
    const normalizedAddress = walletAddress.toLowerCase();
    const gamificationData = await getUserGamificationData(walletAddress);
    
    if (!gamificationData) return null;
    
    const badges = gamificationData.badges || [];
    const calculatedPoints = calculatePointsFromBadges(badges);
    const currentPoints = gamificationData.points || 0;
    
    // Only update if points don't match (to avoid unnecessary writes)
    if (calculatedPoints !== currentPoints) {
      console.log(`🔄 Recalculating points for ${normalizedAddress}: ${currentPoints} -> ${calculatedPoints} (based on ${badges.length} badges)`);
      const gamificationRef = doc(db, 'gamification', normalizedAddress);
      await updateDoc(gamificationRef, {
        points: calculatedPoints,
        updatedAt: serverTimestamp()
      });
      return calculatedPoints;
    }
    
    return currentPoints;
  } catch (error) {
    console.error('Error recalculating points:', error);
    return null;
  }
};

/**
 * Get user's gamification data
 */
export const getUserGamificationData = async (walletAddress) => {
  if (!walletAddress) return null;
  
  try {
    const normalizedAddress = walletAddress.toLowerCase();
    const gamificationRef = doc(db, 'gamification', normalizedAddress);
    const docSnap = await getDoc(gamificationRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      
      // Auto-fix points if they don't match current badge definitions
      // This ensures users get correct points if badge values were changed
      const badges = data.badges || [];
      const calculatedPoints = calculatePointsFromBadges(badges);
      const storedPoints = data.points || 0;
      
      if (calculatedPoints !== storedPoints && badges.length > 0) {
        // Points mismatch detected - recalculate and update
        console.log(`⚠️ Points mismatch detected for ${normalizedAddress}: stored=${storedPoints}, calculated=${calculatedPoints}. Auto-fixing...`);
        await updateDoc(gamificationRef, {
          points: calculatedPoints,
          updatedAt: serverTimestamp()
        });
        return { ...data, points: calculatedPoints };
      }
      
      return data;
    }
    
    // Initialize if doesn't exist
    const initialData = {
      walletAddress: normalizedAddress,
      badges: [],
      achievements: [],
      currentStreak: 0,
      longestStreak: 0,
      lastVisitDate: null,
      totalVisits: 0,
      profileCompleteness: 0,
      points: 0,
      messagesSent: 0,
      profilesViewed: 0,
      claimedXPosts: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(gamificationRef, initialData);
    return initialData;
  } catch (error) {
    console.error('Error getting gamification data:', error);
    return null;
  }
};

/**
 * Update activity streak
 */
export const updateActivityStreak = async (walletAddress) => {
  if (!walletAddress) return null;
  
  try {
    const normalizedAddress = walletAddress.toLowerCase();
    const gamificationRef = doc(db, 'gamification', normalizedAddress);
    const gamificationData = await getUserGamificationData(walletAddress);
    
    if (!gamificationData) return null;
    
    // Use UTC dates with cut-off at 0:00:00 UTC
    const now = new Date();
    // Get today's date at exactly 0:00:00 UTC
    const todayUTC = new Date(Date.UTC(
      now.getUTCFullYear(), 
      now.getUTCMonth(), 
      now.getUTCDate(),
      0,  // hours
      0,  // minutes
      0,  // seconds
      0   // milliseconds
    ));
    
    let lastVisitDateUTC = null;
    if (gamificationData.lastVisitDate) {
      // Handle Firestore Timestamp objects and regular Date objects
      let lastVisitTimestamp;
      
      // Check if it's a Firestore Timestamp object
      if (gamificationData.lastVisitDate instanceof Timestamp) {
        lastVisitTimestamp = gamificationData.lastVisitDate.toDate().getTime();
      } else if (gamificationData.lastVisitDate.toDate && typeof gamificationData.lastVisitDate.toDate === 'function') {
        // Firestore Timestamp with toDate method (older SDK versions)
        lastVisitTimestamp = gamificationData.lastVisitDate.toDate().getTime();
      } else if (gamificationData.lastVisitDate.seconds !== undefined) {
        // Firestore Timestamp with seconds property
        const seconds = gamificationData.lastVisitDate.seconds;
        const nanoseconds = gamificationData.lastVisitDate.nanoseconds || 0;
        lastVisitTimestamp = seconds * 1000 + nanoseconds / 1000000;
      } else if (gamificationData.lastVisitDate instanceof Date) {
        // Already a Date object
        lastVisitTimestamp = gamificationData.lastVisitDate.getTime();
      } else {
        // Try to parse as timestamp string or number
        lastVisitTimestamp = new Date(gamificationData.lastVisitDate).getTime();
      }
      
      // Validate the timestamp
      if (isNaN(lastVisitTimestamp) || lastVisitTimestamp <= 0) {
        console.warn('Invalid lastVisitDate timestamp, treating as first visit');
        lastVisitDateUTC = null;
      } else {
        const lastVisit = new Date(lastVisitTimestamp);
        // Get last visit date at exactly 0:00:00 UTC
        lastVisitDateUTC = new Date(Date.UTC(
          lastVisit.getUTCFullYear(), 
          lastVisit.getUTCMonth(), 
          lastVisit.getUTCDate(),
          0,  // hours
          0,  // minutes
          0,  // seconds
          0   // milliseconds
        ));
      }
    }
    
    // CRITICAL FIX: For first visit (no lastVisitDate), ALWAYS reset streak to 1
    // Don't use existing currentStreak value - it might be incorrect from test data or bugs
    let newStreak;
    let newLongestStreak = gamificationData.longestStreak || 0;
    
    // Check if this is truly a first visit:
    // 1. No lastVisitDate, OR
    // 2. totalVisits is 0 or 1 (newly registered user)
    const totalVisits = gamificationData.totalVisits || 0;
    const isFirstVisit = !lastVisitDateUTC || totalVisits <= 1;
    
    if (isFirstVisit) {
      // First visit - ALWAYS start at 1, regardless of what currentStreak was
      // This fixes the bug where new users show incorrect streak values
      newStreak = 1;
      // For first visit, longestStreak should be at least 1 (or keep existing if higher)
      if (newStreak > newLongestStreak) {
        newLongestStreak = newStreak;
      }
      console.log('🔥 Streak: First visit, starting streak at 1 (ignoring any existing incorrect streak values)', {
        hadLastVisitDate: !!lastVisitDateUTC,
        totalVisits: totalVisits,
        previousStreak: gamificationData.currentStreak
      });
    } else {
      // Not first visit - use existing streak as starting point
      newStreak = gamificationData.currentStreak || 0;
      // Calculate difference in calendar days using UTC dates
      // This is more reliable than using time differences and rounding
      // Convert to days since epoch to handle month/year boundaries correctly
      const todayDays = Math.floor(todayUTC.getTime() / (1000 * 60 * 60 * 24));
      const lastVisitDays = Math.floor(lastVisitDateUTC.getTime() / (1000 * 60 * 60 * 24));
      const daysDiff = todayDays - lastVisitDays;
      
      const previousStreak = gamificationData.currentStreak || 0;
      
      // Add detailed logging for debugging
      console.log('🔥 Streak Calculation Debug:', {
        previousStreak,
        daysDiff,
        todayUTC: todayUTC.toISOString(),
        lastVisitDateUTC: lastVisitDateUTC.toISOString(),
        lastVisitDateRaw: gamificationData.lastVisitDate
      });
      
      if (daysDiff === 0) {
        // Same day, no change
        newStreak = previousStreak;
        console.log(`🔥 Streak: Same day visit, maintaining streak at ${newStreak}`);
      } else if (daysDiff === 1) {
        // Consecutive day
        newStreak = previousStreak + 1;
        console.log(`🔥 Streak: Consecutive day (+1), streak now ${newStreak} (was ${previousStreak})`);
      } else if (daysDiff < 0) {
        // Negative difference (shouldn't happen, but handle gracefully)
        // This could happen if there's a clock/timezone issue
        // Keep current streak
        newStreak = previousStreak;
        console.warn(`🔥 Streak: Negative day difference (${daysDiff}), maintaining streak at ${newStreak}. This may indicate a timezone/clock issue.`);
      } else {
        // Streak broken (more than 1 day difference)
        // Add extra logging for streak breaks to help diagnose the issue
        // Special case: if streak is exactly 7 and daysDiff is exactly 7, this is suspicious
        // It might indicate that lastVisitDate wasn't updated correctly on day 7
        if (previousStreak === 7 && daysDiff === 7) {
          console.error(`🔥 Streak: SUSPICIOUS RESET - Streak was 7, daysDiff is 7. This suggests lastVisitDate may not have been updated correctly. Attempting to recover by treating as consecutive day.`, {
            previousStreak,
            daysDiff,
            todayUTC: todayUTC.toISOString(),
            lastVisitDateUTC: lastVisitDateUTC.toISOString(),
            lastVisitDateRaw: gamificationData.lastVisitDate
          });
          // Try to recover: if the last visit was exactly 7 days ago and streak was 7,
          // it's possible the lastVisitDate wasn't updated. Treat as consecutive day.
          newStreak = previousStreak + 1;
        } else {
          console.warn(`🔥 Streak: Broken (${daysDiff} days since last visit), resetting from ${previousStreak} to 1`, {
            previousStreak,
            daysDiff,
            todayUTC: todayUTC.toISOString(),
            lastVisitDateUTC: lastVisitDateUTC.toISOString()
          });
          newStreak = 1;
        }
      }
    }
    
    if (newStreak > newLongestStreak) {
      newLongestStreak = newStreak;
    }
    
    const updateData = {
      currentStreak: newStreak,
      longestStreak: newLongestStreak,
      lastVisitDate: serverTimestamp(),
      totalVisits: increment(1),
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(gamificationRef, updateData);
    
    // Reload gamification data to get updated totalVisits for FIRST_STEPS badge check
    // (Firestore increment operations are atomic, but we reload to ensure consistency)
    const updatedGamificationData = await getUserGamificationData(walletAddress);
    
    // Check for streak badges and first visit badge
    // checkAndAwardBadges will read totalVisits from the updated gamification data
    await checkAndAwardBadges(walletAddress, { 
      currentStreak: newStreak
    }, null);
    
    return {
      currentStreak: newStreak,
      longestStreak: newLongestStreak
    };
  } catch (error) {
    console.error('Error updating activity streak:', error);
    return null;
  }
};

/**
 * Update profile completeness and check for badges
 */
export const updateProfileCompleteness = async (walletAddress, profile) => {
  if (!walletAddress || !profile) {
    console.warn('⚠️ updateProfileCompleteness: Missing walletAddress or profile', { walletAddress: !!walletAddress, profile: !!profile });
    return null;
  }
  
  try {
    const normalizedAddress = walletAddress.toLowerCase();
    const completeness = calculateProfileCompleteness(profile);
    const gamificationRef = doc(db, 'gamification', normalizedAddress);
    
    console.log('💾 Updating profile completeness in Firestore:', {
      walletAddress: normalizedAddress,
      calculatedCompleteness: completeness,
      previousValue: 'checking...'
    });
    
    // Get current value before updating to log the change
    const currentDoc = await getDoc(gamificationRef);
    const previousCompleteness = currentDoc.exists() ? (currentDoc.data().profileCompleteness || 0) : 0;
    
    await updateDoc(gamificationRef, {
      profileCompleteness: completeness,
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Profile completeness updated in Firestore:', {
      previous: previousCompleteness + '%',
      new: completeness + '%',
      changed: previousCompleteness !== completeness
    });
    
    // Check for profile completeness badges (pass profile for social/tag badge checks)
    await checkAndAwardBadges(walletAddress, { profileCompleteness: completeness }, profile);
    
    return completeness;
  } catch (error) {
    console.error('❌ Error updating profile completeness:', error);
    return null;
  }
};

/**
 * Check and award badges based on criteria
 */
export const checkAndAwardBadges = async (walletAddress, criteria = {}, profile = null) => {
  if (!walletAddress) return [];
  
  try {
    const normalizedAddress = walletAddress.toLowerCase();
    const gamificationData = await getUserGamificationData(walletAddress);
    
    if (!gamificationData) return [];
    
    const existingBadges = gamificationData.badges || [];
    const newBadges = [];
    
    // Check profile completeness badges
    const completeness = criteria.profileCompleteness !== undefined 
      ? criteria.profileCompleteness 
      : gamificationData.profileCompleteness || 0;
    
    if (completeness >= 25 && !existingBadges.includes(BADGES.PROFILE_STARTER.id)) {
      newBadges.push(BADGES.PROFILE_STARTER.id);
    }
    if (completeness >= 50 && !existingBadges.includes(BADGES.PROFILE_BUILDER.id)) {
      newBadges.push(BADGES.PROFILE_BUILDER.id);
    }
    if (completeness >= 75 && !existingBadges.includes(BADGES.PROFILE_MASTER.id)) {
      newBadges.push(BADGES.PROFILE_MASTER.id);
    }
    if (completeness >= 100 && !existingBadges.includes(BADGES.PROFILE_LEGEND.id)) {
      newBadges.push(BADGES.PROFILE_LEGEND.id);
    }
    
    // Check streak badges
    const currentStreak = criteria.currentStreak !== undefined
      ? criteria.currentStreak
      : gamificationData.currentStreak || 0;
    
    if (currentStreak >= 7 && !existingBadges.includes(BADGES.WEEK_WARRIOR.id)) {
      newBadges.push(BADGES.WEEK_WARRIOR.id);
    }
    if (currentStreak >= 30 && !existingBadges.includes(BADGES.MONTH_MASTER.id)) {
      newBadges.push(BADGES.MONTH_MASTER.id);
    }
    if (currentStreak >= 100 && !existingBadges.includes(BADGES.STREAK_CHAMPION.id)) {
      newBadges.push(BADGES.STREAK_CHAMPION.id);
    }
    
    // Check first visit badge
    if (gamificationData.totalVisits === 1 && !existingBadges.includes(BADGES.FIRST_STEPS.id)) {
      newBadges.push(BADGES.FIRST_STEPS.id);
    }
    
    // Check connector badge (first message sent)
    const messagesSent = criteria.messagesSent !== undefined 
      ? criteria.messagesSent 
      : (gamificationData.messagesSent || 0);
    if (messagesSent >= 1 && !existingBadges.includes(BADGES.CONNECTOR.id)) {
      newBadges.push(BADGES.CONNECTOR.id);
    }
    
    // Check explorer badge (50 profiles viewed)
    const profilesViewed = criteria.profilesViewed !== undefined
      ? criteria.profilesViewed
      : (gamificationData.profilesViewed || 0);
    if (profilesViewed >= 50 && !existingBadges.includes(BADGES.EXPLORER.id)) {
      newBadges.push(BADGES.EXPLORER.id);
    }
    
    // Check early adopter badge
    // During beta: automatically award to all users when they enter
    // After beta: stop awarding (set BETA_IS_ACTIVE to false)
    if (BETA_IS_ACTIVE && !existingBadges.includes(BADGES.EARLY_ADOPTER.id)) {
      newBadges.push(BADGES.EARLY_ADOPTER.id);
    }
    
    // Check social badges if profile is provided
    if (profile) {
      const socialLinksCount = Array.isArray(profile.socialLinks) ? profile.socialLinks.length : 0;
      if (socialLinksCount >= 5 && !existingBadges.includes(BADGES.SOCIAL_BUTTERFLY.id)) {
        newBadges.push(BADGES.SOCIAL_BUTTERFLY.id);
      }
      
      const tagsCount = Array.isArray(profile.tagsList) ? profile.tagsList.length : 0;
      if (tagsCount >= 10 && !existingBadges.includes(BADGES.TAG_COLLECTOR.id)) {
        newBadges.push(BADGES.TAG_COLLECTOR.id);
      }
    }
    
    // Check referral badges
    const referralsCount = criteria.referralsCount !== undefined
      ? criteria.referralsCount
      : (gamificationData.referralsCount || 0);
    
    if (referralsCount >= 1 && !existingBadges.includes(BADGES.REFERRAL_STARTER.id)) {
      newBadges.push(BADGES.REFERRAL_STARTER.id);
    }
    if (referralsCount >= 10 && !existingBadges.includes(BADGES.REFERRAL_CHAMPION.id)) {
      newBadges.push(BADGES.REFERRAL_CHAMPION.id);
    }
    if (referralsCount >= 100 && !existingBadges.includes(BADGES.REFERRAL_LEGEND.id)) {
      newBadges.push(BADGES.REFERRAL_LEGEND.id);
    }
    if (referralsCount >= 1000 && !existingBadges.includes(BADGES.REFERRAL_MASTER.id)) {
      newBadges.push(BADGES.REFERRAL_MASTER.id);
    }
    
    // Award new badges
    if (newBadges.length > 0) {
      const gamificationRef = doc(db, 'gamification', normalizedAddress);
      // Calculate total points based on each badge's points value
      const totalPoints = newBadges.reduce((sum, badgeId) => {
        const badgeKey = Object.keys(BADGES).find(key => BADGES[key].id === badgeId);
        const badge = badgeKey ? BADGES[badgeKey] : null;
        return sum + (badge?.points || 10); // Default to 10 if points not defined
      }, 0);
      
      await updateDoc(gamificationRef, {
        badges: [...existingBadges, ...newBadges],
        points: increment(totalPoints),
        updatedAt: serverTimestamp()
      });
      
      return newBadges.map(badgeId => {
        const badgeKey = Object.keys(BADGES).find(key => BADGES[key].id === badgeId);
        return badgeKey ? BADGES[badgeKey] : null;
      }).filter(Boolean);
    }
    
    return [];
  } catch (error) {
    console.error('Error checking badges:', error);
    return [];
  }
};

/**
 * Award badge manually (for social badges, etc.)
 */
export const awardBadge = async (walletAddress, badgeId) => {
  if (!walletAddress || !badgeId) return false;
  
  try {
    const normalizedAddress = walletAddress.toLowerCase();
    const gamificationData = await getUserGamificationData(walletAddress);
    
    if (!gamificationData) return false;
    
    const existingBadges = gamificationData.badges || [];
    
    if (existingBadges.includes(badgeId)) {
      return false; // Already has badge
    }
    
    // Get badge points value
    const badgeKey = Object.keys(BADGES).find(key => BADGES[key].id === badgeId);
    const badge = badgeKey ? BADGES[badgeKey] : null;
    const badgePoints = badge?.points || 10; // Default to 10 if points not defined
    
    const gamificationRef = doc(db, 'gamification', normalizedAddress);
    await updateDoc(gamificationRef, {
      badges: [...existingBadges, badgeId],
      points: increment(badgePoints),
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error awarding badge:', error);
    return false;
  }
};

/**
 * Get leaderboard data
 */
export const getLeaderboard = async (type = 'points', limitCount = 50) => {
  try {
    const gamificationRef = collection(db, 'gamification');
    let leaderboardQuery;
    
    switch (type) {
      case 'streak':
        // Use only currentStreak for ordering to avoid composite index requirement
        // We'll sort by longestStreak in memory if needed
        leaderboardQuery = query(
          gamificationRef,
          orderBy('currentStreak', 'desc'),
          limit(limitCount * 2) // Get more to account for secondary sort
        );
        break;
      case 'completeness':
        leaderboardQuery = query(
          gamificationRef,
          orderBy('profileCompleteness', 'desc'),
          limit(limitCount)
        );
        break;
      case 'badges':
        // For badges, we need to get all and sort by badge count
        leaderboardQuery = query(gamificationRef, limit(limitCount * 2));
        break;
      case 'referral':
        // For referrals, try to use orderBy first (requires index), otherwise fetch all
        // Try orderBy first - if index exists, this is efficient
        leaderboardQuery = query(
          gamificationRef,
          orderBy('referralsCount', 'desc'),
          limit(limitCount * 2) // Get more to filter out zeros
        );
        break;
      default: // points
        leaderboardQuery = query(
          gamificationRef,
          orderBy('points', 'desc'),
          limit(limitCount)
        );
    }
    
    let querySnapshot;
    try {
      querySnapshot = await getDocs(leaderboardQuery);
    } catch (error) {
      // If query fails (e.g., missing index), fetch ALL documents and sort in memory
      if (error.code === 'failed-precondition' || error.message?.includes('index') || type === 'referral' || type === 'badges') {
        console.warn('Firestore index not found or using in-memory sort, fetching all documents for', type, 'leaderboard');
        // For referral and badges, we need ALL documents to find the ones with referrals/badges
        // Remove limit to get all documents (this might be slow for large collections, but necessary for accuracy)
        const fallbackQuery = query(gamificationRef);
        querySnapshot = await getDocs(fallbackQuery);
      } else {
        throw error; // Re-throw if it's a different error
      }
    }
    
    const leaderboard = [];
    
    for (const docSnap of querySnapshot.docs) {
      const data = docSnap.data();
      
      // For badges leaderboard, calculate badge count
      if (type === 'badges') {
        const badgeCount = (data.badges || []).length;
        leaderboard.push({
          walletAddress: data.walletAddress,
          value: badgeCount,
          badges: data.badges || [],
          points: data.points || 0,
          currentStreak: data.currentStreak || 0,
          profileCompleteness: data.profileCompleteness || 0,
          referralsCount: data.referralsCount || 0
        });
      } else if (type === 'referral') {
        // For referral leaderboard, use referralsCount and filter out zeros
        const referralsCount = data.referralsCount || 0;
        // Only include users with at least 1 referral
        if (referralsCount > 0) {
          leaderboard.push({
            walletAddress: data.walletAddress,
            value: referralsCount,
            badges: data.badges || [],
            points: data.points || 0,
            currentStreak: data.currentStreak || 0,
            profileCompleteness: data.profileCompleteness || 0,
            referralsCount: referralsCount
          });
        }
      } else {
        leaderboard.push({
          walletAddress: data.walletAddress,
          value: type === 'streak' ? data.currentStreak || 0 : 
                 type === 'completeness' ? data.profileCompleteness || 0 :
                 data.points || 0,
          badges: data.badges || [],
          points: data.points || 0,
          currentStreak: data.currentStreak || 0,
          profileCompleteness: data.profileCompleteness || 0,
          referralsCount: data.referralsCount || 0
        });
      }
    }
    
    // Sort badges and referral leaderboards
    if (type === 'badges' || type === 'referral') {
      leaderboard.sort((a, b) => b.value - a.value);
      return leaderboard.slice(0, limitCount);
    }
    
    // For streak, do secondary sort by longestStreak if currentStreak is equal
    if (type === 'streak') {
      leaderboard.sort((a, b) => {
        if (b.currentStreak !== a.currentStreak) {
          return b.currentStreak - a.currentStreak;
        }
        return b.longestStreak - a.longestStreak;
      });
      return leaderboard.slice(0, limitCount);
    }
    
    return leaderboard;
  } catch (error) {
    console.error('Error getting leaderboard:', error);
    return [];
  }
};

/**
 * Get user's rank on leaderboard
 */
export const getUserRank = async (walletAddress, type = 'points') => {
  if (!walletAddress) return null;
  
  try {
    const leaderboard = await getLeaderboard(type, 1000); // Get more to find rank
    const normalizedAddress = walletAddress.toLowerCase();
    const rank = leaderboard.findIndex(entry => entry.walletAddress === normalizedAddress);
    
    return rank >= 0 ? rank + 1 : null;
  } catch (error) {
    console.error('Error getting user rank:', error);
    return null;
  }
};

/**
 * Track message sent and check for CONNECTOR badge
 */
export const trackMessageSent = async (walletAddress) => {
  if (!walletAddress) return null;
  
  try {
    const normalizedAddress = walletAddress.toLowerCase();
    const gamificationRef = doc(db, 'gamification', normalizedAddress);
    const gamificationData = await getUserGamificationData(walletAddress);
    
    if (!gamificationData) return null;
    
    const currentMessagesSent = gamificationData.messagesSent || 0;
    const newMessagesSent = currentMessagesSent + 1;
    
    await updateDoc(gamificationRef, {
      messagesSent: newMessagesSent,
      updatedAt: serverTimestamp()
    });
    
    // Reload gamification data to get updated values
    const updatedGamificationData = await getUserGamificationData(walletAddress);
    
    // Check for CONNECTOR badge (first message)
    // Pass updated messagesSent count in criteria
    const newBadgesAwarded = await checkAndAwardBadges(walletAddress, { 
      messagesSent: newMessagesSent,
      profileCompleteness: updatedGamificationData?.profileCompleteness,
      currentStreak: updatedGamificationData?.currentStreak,
      profilesViewed: updatedGamificationData?.profilesViewed
    }, null);
    
    return { messagesSent: newMessagesSent, newBadges: newBadgesAwarded, updatedData: updatedGamificationData };
  } catch (error) {
    console.error('Error tracking message sent:', error);
    return null;
  }
};

/**
 * Track profile viewed and check for EXPLORER badge
 */
export const trackProfileViewed = async (walletAddress) => {
  if (!walletAddress) return null;
  
  try {
    const normalizedAddress = walletAddress.toLowerCase();
    const gamificationRef = doc(db, 'gamification', normalizedAddress);
    const gamificationData = await getUserGamificationData(walletAddress);
    
    if (!gamificationData) return null;
    
    const currentProfilesViewed = gamificationData.profilesViewed || 0;
    const newProfilesViewed = currentProfilesViewed + 1;
    
    await updateDoc(gamificationRef, {
      profilesViewed: newProfilesViewed,
      updatedAt: serverTimestamp()
    });
    
    // Reload gamification data to get updated values
    const updatedGamificationData = await getUserGamificationData(walletAddress);
    
    // Check for EXPLORER badge (50 profiles viewed)
    // Pass updated profilesViewed count in criteria
    const newBadgesAwarded = await checkAndAwardBadges(walletAddress, { 
      profilesViewed: newProfilesViewed,
      profileCompleteness: updatedGamificationData?.profileCompleteness,
      currentStreak: updatedGamificationData?.currentStreak,
      messagesSent: updatedGamificationData?.messagesSent
    }, null);
    
    return { profilesViewed: newProfilesViewed, newBadges: newBadgesAwarded, updatedData: updatedGamificationData };
  } catch (error) {
    console.error('Error tracking profile viewed:', error);
    return null;
  }
};

/**
 * Check if a social link is X.com/Twitter
 */
const isXComLink = (link) => {
  if (!link) return false;
  const normalized = link.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
  return normalized.includes('x.com/') || normalized.includes('twitter.com/');
};

/**
 * Check if a social link is Discord
 */
const isDiscordLink = (link) => {
  if (!link) return false;
  const normalized = link.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
  return normalized.includes('discord.gg/') || normalized.includes('discord.com/');
};

/**
 * Check and award badges for X.com and Discord connections
 */
export const checkSocialConnections = async (walletAddress, socialLinks = []) => {
  if (!walletAddress || !Array.isArray(socialLinks)) return { newBadges: [], pointsAwarded: 0 };
  
  try {
    const normalizedAddress = walletAddress.toLowerCase();
    const gamificationData = await getUserGamificationData(walletAddress);
    
    if (!gamificationData) return { newBadges: [], pointsAwarded: 0 };
    
    const existingBadges = gamificationData.badges || [];
    const newBadges = [];
    let pointsAwarded = 0;
    
    // Check for X.com connection
    const hasXCom = socialLinks.some(link => isXComLink(link));
    if (hasXCom && !existingBadges.includes(BADGES.X_CONNECTOR.id)) {
      newBadges.push(BADGES.X_CONNECTOR.id);
      pointsAwarded += BADGES.X_CONNECTOR.points || 10;
    }
    
    // Check for Discord connection
    const hasDiscord = socialLinks.some(link => isDiscordLink(link));
    if (hasDiscord && !existingBadges.includes(BADGES.DISCORD_CONNECTOR.id)) {
      newBadges.push(BADGES.DISCORD_CONNECTOR.id);
      pointsAwarded += BADGES.DISCORD_CONNECTOR.points || 10;
    }
    
    // Award badges and points if any new badges
    if (newBadges.length > 0) {
      const gamificationRef = doc(db, 'gamification', normalizedAddress);
      await updateDoc(gamificationRef, {
        badges: [...existingBadges, ...newBadges],
        points: increment(pointsAwarded),
        updatedAt: serverTimestamp()
      });
      
      return {
        newBadges: newBadges.map(badgeId => {
          const badgeKey = Object.keys(BADGES).find(key => BADGES[key].id === badgeId);
          return badgeKey ? BADGES[badgeKey] : null;
        }).filter(Boolean),
        pointsAwarded
      };
    }
    
    return { newBadges: [], pointsAwarded: 0 };
  } catch (error) {
    console.error('Error checking social connections:', error);
    return { newBadges: [], pointsAwarded: 0 };
  }
};

/**
 * Award 300 points for posting about PeopleTag on X.com
 * This function should be called when a user submits their X.com post URL
 */
export const awardBullishPost = async (walletAddress, postUrl) => {
  if (!walletAddress || !postUrl) return { success: false, message: 'Wallet address and post URL are required' };
  
  try {
    const normalizedAddress = walletAddress.toLowerCase();
    const gamificationData = await getUserGamificationData(walletAddress);
    
    if (!gamificationData) return { success: false, message: 'Could not load gamification data' };
    
    // Validate that the URL is an X.com/Twitter URL
    const normalizedUrl = postUrl.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
    if (!normalizedUrl.includes('x.com/') && !normalizedUrl.includes('twitter.com/')) {
      return { success: false, message: 'Please provide a valid X.com or Twitter post URL' };
    }
    
    // Check if user already claimed this reward
    const claimedPosts = gamificationData.claimedXPosts || [];
    if (claimedPosts.includes(postUrl)) {
      return { success: false, message: 'You have already claimed points for this post' };
    }
    
    const existingBadges = gamificationData.badges || [];
    const newBadges = [];
    
    // Award BULLISH_POSTER badge if not already awarded
    if (!existingBadges.includes(BADGES.BULLISH_POSTER.id)) {
      newBadges.push(BADGES.BULLISH_POSTER.id);
    }
    
    // Calculate points: 300 points total (badge is included, no extra points for badge)
    const pointsToAward = 300;
    
    // Update gamification data
    const gamificationRef = doc(db, 'gamification', normalizedAddress);
    const updateData = {
      points: increment(pointsToAward),
      claimedXPosts: [...claimedPosts, postUrl], // Track claimed posts
      updatedAt: serverTimestamp()
    };
    
    // Add badge if new
    if (newBadges.length > 0) {
      updateData.badges = [...existingBadges, ...newBadges];
    }
    
    await updateDoc(gamificationRef, updateData);
    
    // Store post link in profile database for verification
    try {
      const { getUsernameFromWallet } = await import('../userMappingService.js');
      const username = await getUsernameFromWallet(walletAddress);
      if (username) {
        const profileRef = doc(db, 'profiles', username);
        const profileSnap = await getDoc(profileRef);
        if (profileSnap.exists()) {
          const profileData = profileSnap.data();
          const claimedPostsInProfile = profileData.claimedXPosts || [];
          if (!claimedPostsInProfile.includes(postUrl)) {
            await updateDoc(profileRef, {
              // Required for Firestore rules validation
              _ownerAddress: walletAddress.toLowerCase(),
              username: username,
              claimedXPosts: [...claimedPostsInProfile, postUrl],
              updatedAt: serverTimestamp()
            });
          }
        }
      }
    } catch (error) {
      console.error('Error storing post link in profile:', error);
      // Don't fail the whole operation if profile update fails
    }
    
    return {
      success: true,
      message: `Successfully awarded ${pointsToAward} points${newBadges.length > 0 ? ' and a badge' : ''} for your X.com post!`,
      newBadges: newBadges.map(badgeId => {
        const badgeKey = Object.keys(BADGES).find(key => BADGES[key].id === badgeId);
        return badgeKey ? BADGES[badgeKey] : null;
      }).filter(Boolean),
      pointsAwarded: pointsToAward
    };
  } catch (error) {
    console.error('Error awarding bullish post:', error);
    return { success: false, message: 'Error processing your request. Please try again.' };
  }
};

/**
 * Generate a unique random 6-letter referral code (case-insensitive)
 * Letters only (A-Z, a-z), no numbers
 */
const generateRandomReferralCode = () => {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += letters.charAt(Math.floor(Math.random() * letters.length));
  }
  return code;
};

/**
 * Check if a referral code already exists in the database (case-insensitive)
 */
const referralCodeExists = async (code) => {
  try {
    const gamificationRef = collection(db, 'gamification');
    const querySnapshot = await getDocs(gamificationRef);
    
    const normalizedCode = code.toUpperCase();
    for (const docSnap of querySnapshot.docs) {
      const data = docSnap.data();
      if (data.referralCode && data.referralCode.toUpperCase() === normalizedCode) {
        return true;
      }
    }
    return false;
  } catch (error) {
    console.error('Error checking referral code existence:', error);
    return false;
  }
};

/**
 * Generate a unique referral code for a user
 * Generates random 6-letter codes until finding one that doesn't exist
 */
export const generateReferralCode = async () => {
  let attempts = 0;
  const maxAttempts = 100; // Prevent infinite loop
  
  while (attempts < maxAttempts) {
    const code = generateRandomReferralCode();
    const exists = await referralCodeExists(code);
    
    if (!exists) {
      // Store in uppercase for consistency (but accept any case)
      return code.toUpperCase();
    }
    
    attempts++;
  }
  
  // Fallback: if we can't find a unique code after many attempts, use timestamp-based with letters only
  console.warn('Could not generate unique referral code after many attempts, using fallback');
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const timestamp = Date.now();
  // Use timestamp to seed random letters
  let fallbackCode = '';
  for (let i = 0; i < 6; i++) {
    const index = (timestamp + i) % letters.length;
    fallbackCode += letters[index];
  }
  return fallbackCode;
};

/**
 * Get or create referral code for a user
 * Migrates old wallet-based codes (12 chars) to new random codes (6 letters)
 */
export const getOrCreateReferralCode = async (walletAddress) => {
  if (!walletAddress) return null;
  
  try {
    const normalizedAddress = walletAddress.toLowerCase();
    const gamificationRef = doc(db, 'gamification', normalizedAddress);
    const gamificationData = await getUserGamificationData(walletAddress);
    
    if (!gamificationData) return null;
    
    // If referral code already exists, check if it's in old format
    if (gamificationData.referralCode) {
      const existingCode = gamificationData.referralCode;
      // Check if it's an old wallet-based code (12 characters, hex-like)
      // New codes are 6 letters only
      const isOldFormat = existingCode.length === 12 && /^[0-9A-F]{12}$/i.test(existingCode);
      
      if (isOldFormat) {
        // Migrate to new random code format
        console.log(`🔄 Migrating old referral code ${existingCode} to new format for wallet ${normalizedAddress}`);
        const newCode = await generateReferralCode();
        await updateDoc(gamificationRef, {
          referralCode: newCode,
          updatedAt: serverTimestamp()
        });
        // Old code will still work due to backwards compatibility in processReferral
        return newCode;
      } else {
        // Already using new format, return it
        return existingCode;
      }
    }
    
    // No referral code exists, generate and save new random code
    const referralCode = await generateReferralCode();
    await updateDoc(gamificationRef, {
      referralCode: referralCode,
      referralsCount: 0,
      referralsList: [],
      updatedAt: serverTimestamp()
    });
    
    return referralCode;
  } catch (error) {
    console.error('Error getting/creating referral code:', error);
    return null;
  }
};

/**
 * Generate old wallet-based referral code (for backwards compatibility)
 * Format: first 8 chars (after 0x) + last 4 chars = 12 chars total, uppercase
 */
const generateOldWalletBasedCode = (walletAddress) => {
  if (!walletAddress) return null;
  const normalized = walletAddress.toLowerCase();
  // Skip "0x" prefix (chars 0-1), take chars 2-10 (8 chars) + last 4 chars
  const code = normalized.substring(2, 10) + normalized.substring(normalized.length - 4);
  return code.toUpperCase();
};

/**
 * Process a referral when a new user signs up
 * Awards 10 points to the referrer
 * Supports both new random codes (6 letters) and old wallet-based codes (12 chars) for backwards compatibility
 */
export const processReferral = async (newUserWalletAddress, referralCode) => {
  if (!newUserWalletAddress || !referralCode) {
    return { success: false, message: 'Wallet address and referral code are required' };
  }
  
  try {
    const normalizedNewUser = newUserWalletAddress.toLowerCase();
    
    // Find the referrer by referral code (case-insensitive comparison)
    const gamificationRef = collection(db, 'gamification');
    const querySnapshot = await getDocs(gamificationRef);
    
    // Normalize the input referral code for case-insensitive comparison
    const normalizedInputCode = referralCode.toUpperCase();
    
    let referrerWalletAddress = null;
    
    // First, try to match as a new random code (6 letters, stored in referralCode field)
    for (const docSnap of querySnapshot.docs) {
      const data = docSnap.data();
      // Case-insensitive comparison for new codes
      if (data.referralCode && data.referralCode.toUpperCase() === normalizedInputCode) {
        referrerWalletAddress = data.walletAddress;
        break;
      }
    }
    
    // If not found as new code, try old wallet-based code format (12 chars)
    // Check if input code matches the old format (12 characters, hex-like)
    if (!referrerWalletAddress && normalizedInputCode.length === 12 && /^[0-9A-F]{12}$/i.test(normalizedInputCode)) {
      // Generate old-style code for each wallet and compare
      for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        const walletAddress = data.walletAddress;
        if (walletAddress) {
          const oldCode = generateOldWalletBasedCode(walletAddress);
          if (oldCode === normalizedInputCode) {
            referrerWalletAddress = walletAddress;
            break;
          }
        }
      }
    }
    
    if (!referrerWalletAddress) {
      return { success: false, message: 'Invalid referral code' };
    }
    
    // Prevent self-referral
    if (referrerWalletAddress.toLowerCase() === normalizedNewUser) {
      return { success: false, message: 'Cannot refer yourself' };
    }
    
    // Check if this user was already referred by this referrer
    const referrerGamificationRef = doc(db, 'gamification', referrerWalletAddress.toLowerCase());
    const referrerData = await getDoc(referrerGamificationRef);
    
    if (referrerData.exists()) {
      const referrerGamificationData = referrerData.data();
      const referralsList = referrerGamificationData.referralsList || [];
      
      // Check if this user was already referred
      if (referralsList.includes(normalizedNewUser)) {
        return { success: false, message: 'This user was already referred' };
      }
      
      // Award 10 points to referrer
      const referralsCount = (referrerGamificationData.referralsCount || 0) + 1;
      await updateDoc(referrerGamificationRef, {
        referralsCount: referralsCount,
        referralsList: [...referralsList, normalizedNewUser],
        points: increment(10),
        updatedAt: serverTimestamp()
      });
      
      // Check for referral badges
      await checkAndAwardBadges(referrerWalletAddress, { referralsCount: referralsCount }, null);
      
      // Store referral info for the new user
      const newUserGamificationRef = doc(db, 'gamification', normalizedNewUser);
      const newUserData = await getDoc(newUserGamificationRef);
      if (newUserData.exists()) {
        await updateDoc(newUserGamificationRef, {
          referredBy: referrerWalletAddress.toLowerCase(),
          referredByCode: normalizedInputCode, // Store normalized (uppercase) version
          updatedAt: serverTimestamp()
        });
      }
      
      return {
        success: true,
        message: 'Referral processed successfully',
        referrerWalletAddress: referrerWalletAddress,
        pointsAwarded: 10
      };
    }
    
    return { success: false, message: 'Referrer not found' };
  } catch (error) {
    console.error('Error processing referral:', error);
    return { success: false, message: 'Error processing referral. Please try again.' };
  }
};

/**
 * Get referral stats for a user
 */
export const getReferralStats = async (walletAddress) => {
  if (!walletAddress) return null;
  
  try {
    const gamificationData = await getUserGamificationData(walletAddress);
    if (!gamificationData) return null;
    
    return {
      referralCode: gamificationData.referralCode || null,
      referralsCount: gamificationData.referralsCount || 0,
      referralsList: gamificationData.referralsList || [],
      referredBy: gamificationData.referredBy || null,
      referredByCode: gamificationData.referredByCode || null
    };
  } catch (error) {
    console.error('Error getting referral stats:', error);
    return null;
  }
};

